import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import {
  BtnBold,
  BtnItalic,
  Editor,
  EditorProvider,
  Toolbar,
  BtnBulletList,
  BtnClearFormatting,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  Separator,
} from 'react-simple-wysiwyg';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { proHydroApi } from '../../../helpers/crude_api_helper';

// Backend entity: TemplateInformation ("Klausa Information").
// Endpoints per api-contract-klausa-informasi.md (base {host}/v1):
//   list   GET    /v1/TemplateInformations   (paginated: page/pageSize/searchText/sortField/sortOrder)
//   create POST   /v1/TemplateInformations
//   update PUT    /v1/TemplateInformations/{id}   (route id must equal body id)
//   delete DELETE /v1/TemplateInformations/{id}   (soft delete, 204)
// Field mapping: API `contents` (HTML) <-> UI "Content"; `title` <-> "Title";
//   `templateInformationCategoryId` <-> dropdown "Klausa Category".
const ENDPOINT = '/v1/TemplateInformations';
// Lookup endpoints per api-perbaikan.md (2026-07-27): dropdown options now come
// from these dedicated, server-filtered lookups instead of being derived
// client-side from a wide TemplateInformationCategories page. Shape: { items: [{ id, text }] }.
const JENIS_PENGADAAN_LOOKUP = '/v1/JenisPengadaans/Lookup';
const CATEGORY_LOOKUP = '/v1/TemplateInformationCategories/Lookup';

// Server-side sort is only supported on scalar columns (contract whitelist), and
// sortField must be PascalCase DTO property names — wrong casing returns 400.
// Join columns (Jenis Pengadaan / Category name) are NOT server-sortable/searchable.
const SORT_TITLE = 'Title';

// Strip HTML from `contents` — used only to validate that Content is non-empty
// (the list now renders the real HTML instead of a stripped text preview).
const stripHtml = html =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Editor diganti ke react-simple-wysiwyg (2026-09-02, seragam dengan Planning
// & TenderOpening — tanpa badge "Powered by CKEditor"). Konten lama yang sudah
// menyimpan gambar base64 tetap dirender di editor; hanya tombol insert-image
// yang tidak tersedia lagi.

const emptyForm = {
  id: null,
  jenisPengadaanId: '',
  jenisPengadaanName: '',
  templateInformationCategoryId: '',
  templateInformationCategoryName: '',
  title: '',
  contents: '',
};

const ClausaInformationView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jenisPengadaanOptions, setJenisPengadaanOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Server-side pagination / search / sort state.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(SORT_TITLE);
  const [sortOrder, setSortOrder] = useState('Asc');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  document.title = 'Clausa Information';

  // Debounce the search box, then reset to page 1 for a fresh server query.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await proHydroApi.get(ENDPOINT, {
        page,
        pageSize,
        searchText: debouncedSearch || undefined,
        sortField: sortField || undefined,
        sortOrder: sortField ? sortOrder : undefined,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortField, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchJenisPengadaan = async () => {
    try {
      const result = await proHydroApi.get(JENIS_PENGADAAN_LOOKUP);
      setJenisPengadaanOptions(
        Array.isArray(result?.items) ? result.items : []
      );
    } catch (error) {
      toast.error(`Failed to load Jenis Pengadaan: ${error}`);
      setJenisPengadaanOptions([]);
    }
  };

  useEffect(() => {
    fetchJenisPengadaan();
  }, []);

  // Category options are fetched server-filtered whenever the selected Jenis
  // Pengadaan changes (handleChange already clears templateInformationCategoryId).
  const fetchCategoryLookup = useCallback(async jenisPengadaanId => {
    if (!jenisPengadaanId) {
      setCategoryOptions([]);
      return;
    }
    try {
      const result = await proHydroApi.get(CATEGORY_LOOKUP, {
        JenisPengadaanId: jenisPengadaanId,
      });
      setCategoryOptions(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      toast.error(`Failed to load Clausa Category: ${error}`);
      setCategoryOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchCategoryLookup(formData.jenisPengadaanId);
  }, [formData.jenisPengadaanId, fetchCategoryLookup]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'jenisPengadaanId') {
      // Reset category when Jenis Pengadaan changes to keep the pair consistent.
      setFormData({
        ...formData,
        jenisPengadaanId: value,
        jenisPengadaanName: '',
        templateInformationCategoryId: '',
        templateInformationCategoryName: '',
      });
      setErrors({
        ...errors,
        jenisPengadaanId: '',
        templateInformationCategoryId: '',
      });
      return;
    }
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  // react-simple-wysiwyg mengirim HTML lewat e.target.value (bukan editor.getData()).
  const handleContentChange = e => {
    const data = e.target.value;
    setFormData(prev => ({ ...prev, contents: data }));
    if (data) setErrors(prev => ({ ...prev, contents: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.jenisPengadaanId) {
      newErrors.jenisPengadaanId = 'Jenis Pengadaan is required';
    }
    if (!formData.templateInformationCategoryId) {
      newErrors.templateInformationCategoryId = 'Clausa Category is required';
    }
    if (!formData.title || !formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    // Contract: `contents` is NotEmpty (server returns 400 otherwise).
    if (!stripHtml(formData.contents)) {
      newErrors.contents = 'Content is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setModalForm(true);
  };

  const handleUpdateModal = row => {
    setFormData({
      id: row.id,
      jenisPengadaanId: row.jenisPengadaanId || '',
      jenisPengadaanName: row.jenisPengadaanName || '',
      templateInformationCategoryId: row.templateInformationCategoryId || '',
      templateInformationCategoryName:
        row.templateInformationCategoryName || '',
      title: row.title || '',
      contents: row.contents || '',
    });
    setErrors({});
    setModalForm(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);

    if (formData.id) {
      try {
        // Route id must match body id (contract: MismatchException otherwise).
        await proHydroApi.put(`${ENDPOINT}/${formData.id}`, {
          id: formData.id,
          templateInformationCategoryId: formData.templateInformationCategoryId,
          title: formData.title.trim(),
          contents: formData.contents,
        });
        toast.success('Clausa Information has been updated');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to update data: ${error}`);
      } finally {
        setSaving(false);
      }
    } else {
      try {
        await proHydroApi.create(ENDPOINT, {
          templateInformationCategoryId: formData.templateInformationCategoryId,
          title: formData.title.trim(),
          contents: formData.contents,
        });
        toast.success('Clausa Information has been added');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeleteModal = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await proHydroApi.delete(`${ENDPOINT}/${selectedRow.id}`);
      toast.success('Clausa Information has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  // Server-side sort: only fires for columns that carry a `sortField` (Title).
  const handleSort = (column, sortDirection) => {
    if (!column?.sortField) return;
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 'Desc' : 'Asc');
    setPage(1);
  };

  // A row being edited can point at a soft-deleted Jenis Pengadaan/Category
  // that the Lookup (filtered to active records) won't return — surface it
  // so editing such a row doesn't silently blank the field.
  const jenisPengadaanSelectOptions = useMemo(() => {
    const known = jenisPengadaanOptions.some(
      o => o.id === formData.jenisPengadaanId
    );
    if (formData.jenisPengadaanId && !known) {
      return [
        {
          id: formData.jenisPengadaanId,
          text: formData.jenisPengadaanName || '(current)',
        },
        ...jenisPengadaanOptions,
      ];
    }
    return jenisPengadaanOptions;
  }, [
    jenisPengadaanOptions,
    formData.jenisPengadaanId,
    formData.jenisPengadaanName,
  ]);

  const categorySelectOptions = useMemo(() => {
    const known = categoryOptions.some(
      o => o.id === formData.templateInformationCategoryId
    );
    if (formData.templateInformationCategoryId && !known) {
      return [
        {
          id: formData.templateInformationCategoryId,
          text: formData.templateInformationCategoryName || '(current)',
        },
        ...categoryOptions,
      ];
    }
    return categoryOptions;
  }, [
    categoryOptions,
    formData.templateInformationCategoryId,
    formData.templateInformationCategoryName,
  ]);

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      name: headerLabel('Jenis Pengadaan'),
      selector: row => row.jenisPengadaanName,
      wrap: true,
    },
    {
      name: headerLabel('Category'),
      selector: row => row.templateInformationCategoryName,
      wrap: true,
    },
    {
      name: headerLabel('Title'),
      selector: row => row.title,
      sortable: true,
      sortField: SORT_TITLE,
      wrap: true,
    },
    {
      name: headerLabel('Content'),
      selector: row => stripHtml(row.contents),
      wrap: true,
      grow: 2,
      // Render the raw HTML (images, tables, formatting) instead of a stripped
      // text preview. Constrained to a scrollable box so tall content / large
      // base64 images don't blow up the row height (see clausa-html-cell CSS).
      cell: row =>
        stripHtml(row.contents) ? (
          <div
            className='clausa-html-cell'
            dangerouslySetInnerHTML={{ __html: row.contents }}
          />
        ) : (
          <span className='text-muted'>—</span>
        ),
    },
    {
      name: headerLabel('Action'),
      width: '110px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleUpdateModal(row)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDeleteModal(row)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      {/* Constrain rendered HTML in the Content column so tall content and large
          base64 images stay inside a scrollable box instead of stretching rows. */}
      <style>{`
        .clausa-html-cell { width: 100%; max-height: 120px; overflow: auto; padding: 4px 0; }
        .clausa-html-cell img { max-width: 100%; height: auto; }
        .clausa-html-cell table { max-width: 100%; border-collapse: collapse; }
        .clausa-html-cell table td, .clausa-html-cell table th { border: 1px solid #dee2e6; padding: 2px 4px; }
        .clausa-html-cell p { margin: 0 0 4px; }
        .clausa-html-cell p:last-child { margin-bottom: 0; }
      `}</style>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='Clausa Information' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='ClausaInformationList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Clausa Information
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search title / content...'
                            value={searchKeyword}
                            onChange={e => setSearchKeyword(e.target.value)}
                          />
                        </div>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={handleNewModal}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>
                            Add Clausa Information
                          </span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalCount}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={p => setPage(p)}
                      onChangeRowsPerPage={(newPerPage, p) => {
                        setPageSize(newPerPage);
                        setPage(p);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No clausa information found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus={true}
            centered
            id='addClausaInformationModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='lg'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold'>
                {formData.id !== null
                  ? 'Edit Clausa Information'
                  : 'Add Clausa Information'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12} md={6}>
                      <div className='mb-3'>
                        <Label for='jenisPengadaanId' className='form-label'>
                          Jenis Pengadaan <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='select'
                          id='jenisPengadaanId'
                          name='jenisPengadaanId'
                          className='form-control'
                          invalid={!!errors.jenisPengadaanId}
                          value={formData.jenisPengadaanId || ''}
                          onChange={handleChange}
                        >
                          <option value=''>Select Jenis Pengadaan</option>
                          {jenisPengadaanSelectOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.text}
                            </option>
                          ))}
                        </Input>
                        {errors.jenisPengadaanId && (
                          <div className='invalid-feedback d-block'>
                            {errors.jenisPengadaanId}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className='mb-3'>
                        <Label
                          for='templateInformationCategoryId'
                          className='form-label'
                        >
                          Clausa Category <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='select'
                          id='templateInformationCategoryId'
                          name='templateInformationCategoryId'
                          className='form-control'
                          invalid={!!errors.templateInformationCategoryId}
                          value={formData.templateInformationCategoryId || ''}
                          onChange={handleChange}
                          disabled={!formData.jenisPengadaanId}
                        >
                          <option value=''>Select Clausa Category</option>
                          {categorySelectOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.text}
                            </option>
                          ))}
                        </Input>
                        {errors.templateInformationCategoryId && (
                          <div className='invalid-feedback d-block'>
                            {errors.templateInformationCategoryId}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='title' className='form-label'>
                          Title <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='title'
                          name='title'
                          className='form-control'
                          maxLength={100}
                          invalid={!!errors.title}
                          value={formData.title || ''}
                          onChange={handleChange}
                        />
                        {errors.title && (
                          <div className='invalid-feedback d-block'>
                            {errors.title}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label className='form-label'>
                          Content <span className='text-danger'>*</span>
                        </Label>
                        <EditorProvider>
                          <Editor
                            value={formData.contents || ''}
                            onChange={handleContentChange}
                            style={{ height: '220px', overflowY: 'auto' }}
                            className='form-control bg-white'
                          >
                            <Toolbar>
                              <BtnStyles />
                              <BtnBold />
                              <BtnItalic />
                              <BtnUnderline />
                              <BtnStrikeThrough />
                              <Separator />
                              <BtnBulletList />
                              <BtnNumberedList />
                              <Separator />
                              <BtnLink />
                              <Separator />
                              <BtnUndo />
                              <BtnRedo />
                              <Separator />
                              <BtnClearFormatting />
                            </Toolbar>
                          </Editor>
                        </EditorProvider>
                        {errors.contents && (
                          <div className='invalid-feedback d-block'>
                            {errors.contents}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='light'
                className='btn-sm'
                onClick={() => setModalForm(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                color='primary'
                className='btn-sm'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ClausaInformationView;
